import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { Collection } from 'constants/index'
import firebase from 'firebase/app'
import { cloudFunctionsInstance } from 'config/axios'
import { isConferenceLive, getAssignedConferenceIds, isConferenceUser } from 'utils/conference'

export const initialState = {
  error: null,
  inProgress: false,
  loginInProgress: false, // true while signIn thunk is running — gates onAuthStateChanged
  user: null,
  isAuthenticated: false,
  conferenceAssignments: [] // [{ conferenceId, confName, role }] — populated on login for multi-conf admins
}

export const signOut = createAsyncThunk(
  'user/signOut',
  async (requestData, { rejectWithValue }) => {
    try {
      await firebase.auth().signOut()
    } catch (error) {
      return rejectWithValue(error)
    }
  }
)

const authSlice = createSlice({
  name: 'auth',
  initialState: initialState,
  reducers: {
    setError: (state, action) => {
      state.error = action.payload
    },
    setProgress: (state, action) => {
      state.inProgress = action.payload
    },
    setUser: (state, action) => {
      state.user = action.payload
    },
    setAuthenticated: (state, action) => {
      state.isAuthenticated = action.payload
    },
    setLoginInProgress: (state, action) => {
      state.loginInProgress = action.payload
    },
    setConferenceAssignments: (state, action) => {
      state.conferenceAssignments = action.payload
    },
    // Updates auth.user.conferenceId and role to the selected assignment
    switchConference: (state, action) => {
      const { conferenceId, role } = action.payload
      if (state.user) {
        state.user = { ...state.user, conferenceId, role }
      }
    }
  },
  extraReducers: {
    [signOut.fulfilled]: (state) => {
      state.error = null
      state.inProgress = false
      state.user = null
      state.isAuthenticated = false
      state.conferenceAssignments = []
    }
  }
})

// Shared by signIn (email/password) and signInWithCustomToken (activation-link
// auto-login) — everything after Firebase Auth has confirmed who the user is.
export const resolveUserAfterAuth = (user) => async (dispatch) => {
  const userSnapshot = await firebase.firestore().collection(Collection.USERS).doc(user.id).get()
  const userResponse = userSnapshot.data()

  if (!userResponse) {
    dispatch(authSlice.actions.setLoginInProgress(false))
    return { error: 'Account not found. Please contact your administrator.' }
  }

  if (isConferenceUser(userResponse)) {
    // conferenceIds is the new array field; falls back to legacy scalar conferenceId
    const assignedIds = getAssignedConferenceIds(userResponse)

    if (!assignedIds.length) {
      dispatch(authSlice.actions.setLoginInProgress(false))
      return { error: 'No conference assigned to your account.' }
    }

    // Fetch all assigned conferences in parallel
    const confSnapshots = await Promise.all(
      assignedIds.map((id) => firebase.firestore().collection(Collection.CONFERENCES).doc(id).get())
    )

    const activeConfs = confSnapshots
      .map((snap) => ({ id: snap.id, ...snap.data() }))
      .filter(isConferenceLive)

    if (!activeConfs.length) {
      dispatch(authSlice.actions.setLoginInProgress(false))
      return { error: 'You cannot login because your Conference is disabled.' }
    }

    // Build the assignments list with per-conference roles and expiry flag
    const now = new Date()
    const assignments = activeConfs.map((c) => {
      const rawUntil = c.viewableUntil
      const until = rawUntil?.toDate ? rawUntil.toDate() : rawUntil ? new Date(rawUntil) : null
      return {
        conferenceId: c.id,
        confName: c.confName,
        role: userResponse.conferenceRoles?.[c.id] ?? userResponse.role,
        isExpired: until ? until < now : false
      }
    })

    const liveAssignments = assignments.filter((a) => !a.isExpired)
    if (!liveAssignments.length) {
      dispatch(authSlice.actions.setLoginInProgress(false))
      const msg =
        assignments.length === 1
          ? 'This conference has expired. Please contact your administrator.'
          : 'All your conferences have expired. Please contact your administrator.'
      return { error: msg }
    }

    dispatch(authSlice.actions.setConferenceAssignments(assignments))

    if (assignments.length > 1) {
      // Don't auto-select — caller will show a conference picker.
      // Flag stays true until the picker confirm dispatches setLoginInProgress(false).
      return { needsPicker: true, assignments, partialUser: { ...user, ...userResponse } }
    }

    // Single conference — auto-select
    const fullUser = {
      ...user,
      ...userResponse,
      conferenceId: assignments[0].conferenceId,
      role: assignments[0].role
    }
    dispatch(authSlice.actions.setLoginInProgress(false))
    dispatch(authSlice.actions.setUser(fullUser))
    dispatch(authSlice.actions.setAuthenticated(true))
    return { needsPicker: false, user: fullUser, assignments }
  } else {
    const fullUser = { ...user, ...userResponse }
    dispatch(authSlice.actions.setLoginInProgress(false))
    dispatch(authSlice.actions.setUser(fullUser))
    dispatch(authSlice.actions.setAuthenticated(true))
    return { needsPicker: false, user: fullUser, assignments: [] }
  }
}

export const signIn = (email, password) => async (dispatch) => {
  // Set flag BEFORE the firebase call so onAuthStateChanged skips its own user-setup
  // while signIn is still running (it fires mid-await otherwise).
  dispatch(authSlice.actions.setLoginInProgress(true))
  try {
    const response = await firebase.auth().signInWithEmailAndPassword(email, password)
    const user = {
      id: response.user.uid,
      email: response.user.email
    }
    return await dispatch(resolveUserAfterAuth(user))
  } catch (err) {
    console.error('Login error:', err)
    dispatch(authSlice.actions.setLoginInProgress(false))
    const firebaseAuthErrors = {
      'auth/user-disabled': 'Your account has been disabled. Please contact support.',
      'auth/user-not-found': 'No account found with this email address.',
      'auth/wrong-password': 'Incorrect password. Please try again.',
      'auth/invalid-email': 'Invalid email address format.',
      'auth/invalid-credential': 'Incorrect email or password. Please try again.',
      'auth/too-many-requests':
        'Too many failed attempts. Please try again later or reset your password.',
      'auth/network-request-failed': 'Network error. Please check your connection and try again.',
      'auth/email-already-in-use': 'An account with this email already exists.'
    }
    const message = firebaseAuthErrors[err.code] || 'Incorrect Email or Password, Please try again.'
    return { error: message }
  }
}

// Used by the activation flow (set-password page) to log the user in right
// after they set their real password, via the customToken the backend issued.
export const signInWithCustomToken = (customToken) => async (dispatch) => {
  dispatch(authSlice.actions.setLoginInProgress(true))
  try {
    const response = await firebase.auth().signInWithCustomToken(customToken)
    const user = {
      id: response.user.uid,
      email: response.user.email
    }
    return await dispatch(resolveUserAfterAuth(user))
  } catch (err) {
    console.error('Activation sign-in error:', err)
    dispatch(authSlice.actions.setLoginInProgress(false))
    return {
      error: 'Your password was set, but we could not sign you in automatically. Please log in.'
    }
  }
}

export const verifyGoogleRecaptcha = (userResponse) => async (dispatch) => {
  try {
    const response = await cloudFunctionsInstance.post('/verifyRecaptchaResponse', {
      userResponse
    })
    return response.data?.data?.isCaptchaVerified || false
  } catch (err) {
    dispatch(authSlice.actions.setError(err))
  }
}

export default authSlice
